import { useEffect, useRef, useState, type JSX } from "react";
import { useAuth } from "../../context/authCore";
import { auth } from "../../config/firebase";
import { deleteUser } from "firebase/auth";
import UserDetails from "./UserDetails";
// ProfileModal is rendered by the parent (UserProfile) to avoid clipping/styling issues

type UserMenuProps = {
  onClose: () => void;
  onOpenProfile?: () => void;
};

const UserMenu = ({ onClose, onOpenProfile }: UserMenuProps): JSX.Element => {
  const { signOut, refreshClaims } = useAuth();
  const [showConfirm, setShowConfirm] = useState(false);
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  // profile modal is rendered by parent; menu only notifies via onOpenProfile
  const toastTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!toastMsg) return;
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current);
    }
    toastTimerRef.current = window.setTimeout(
      () => setToastMsg(null),
      4200,
    ) as unknown as number;
    return () => {
      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    };
  }, [toastMsg]);
  const menuWidth = "min(210px, calc(100vw - 1.25rem))";

  return (
    <div
      style={{
        position: "relative",
        marginTop: "0.35rem",
        zIndex: 1000,
        width: menuWidth,
        minWidth: 140,
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "0.25rem",
          padding: "0.5rem",
          borderRadius: 12,
          background: "rgba(6, 8, 12, 0.92)",
          border: "1px solid rgba(255,255,255,0.08)",
          boxShadow: "0 12px 32px rgba(0,0,0,0.45)",
          backdropFilter: "blur(12px)",
        }}
      >
        <div style={{ padding: "0.25rem 0" }}>
          {/* user details will be fetched below */}
          <UserDetails />
        </div>
        <div style={{ height: 1, background: "rgba(255,255,255,0.04)" }} />
        <MenuButton
          label="Delete Account"
          onClick={() => setShowConfirm(true)}
        />
        <MenuButton
          label="Profile"
          onClick={() => {
            // Notify parent to open the profile modal, then close this menu.
            try {
              if (onOpenProfile) onOpenProfile();
            } catch {
              // ignore
            }
            onClose();
          }}
        />
        <div
          style={{
            height: 1,
            background: "rgba(255,255,255,0.08)",
          }}
        />
        <MenuButton
          label={isRefreshing ? "Refreshing..." : "Refresh Access"}
          onClick={async () => {
            if (isRefreshing || !refreshClaims) return;
            setIsRefreshing(true);
            try {
              await refreshClaims();
              setToastMsg(
                "Access refreshed successfully! The admin tab should now be visible if you have permissions.",
              );
            } catch (e) {
              console.warn("Refresh claims failed:", e);
              setToastMsg(
                "Failed to refresh access. Please try signing out and back in.",
              );
            } finally {
              setIsRefreshing(false);
            }
          }}
        />
        <MenuButton
          label="Logout"
          onClick={async () => {
            try {
              if (signOut) await signOut();
            } catch (e) {
              // eslint-disable-next-line no-console
              console.warn("Sign out failed:", e);
            }
            onClose();
          }}
        />
      </div>
      {/* Confirm modal */}
      {showConfirm && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 4000,
            padding: "1rem",
            backgroundColor: "rgba(0,0,0,0.6)",
          }}
          onClick={() => setShowConfirm(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(420px, calc(100vw - 2rem))",
              background: "#050607",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 12,
              padding: "1.25rem clamp(1rem, 4vw, 1.5rem)",
              boxShadow: "0 30px 60px rgba(0,0,0,0.6)",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <h5 style={{ margin: 0, color: "#fff" }}>Delete account?</h5>
            <p style={{ color: "#c7cbd1", marginTop: 8 }}>
              This action is permanent and cannot be undone. Are you sure you
              want to delete your account?
            </p>
            <div
              style={{
                display: "flex",
                gap: 8,
                justifyContent: "flex-end",
                marginTop: 12,
                flexWrap: "wrap",
              }}
            >
              <button
                className="btn btn-outline-light"
                onClick={() => setShowConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={async () => {
                  try {
                    const current = auth?.currentUser as any;
                    if (!current) {
                      setToastMsg("No authenticated user found.");
                      setShowConfirm(false);
                      onClose();
                      return;
                    }

                    await deleteUser(current);
                    setToastMsg("Your account has been deleted.");
                  } catch (e: any) {
                    if (e && e.code === "auth/requires-recent-login") {
                      setToastMsg(
                        "Please sign in again before deleting your account.",
                      );
                    } else {
                      // eslint-disable-next-line no-console
                      console.warn("Delete account failed:", e);
                      setToastMsg(
                        "Failed to delete account. Please try again later.",
                      );
                    }
                  }

                  try {
                    if (signOut) await signOut();
                  } catch (err) {
                    // eslint-disable-next-line no-console
                    console.warn("Sign out after delete failed:", err);
                  }

                  setShowConfirm(false);
                  onClose();
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Profile modal is owned by the parent and rendered at a higher level */}

      {/* Toast */}
      {toastMsg && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: "fixed",
            right: "clamp(12px, 5vw, 24px)",
            left: "auto",
            bottom: "clamp(12px, 5vw, 24px)",
            zIndex: 5000,
            background: "rgba(20,20,20,0.95)",
            color: "#fff",
            padding: "0.75rem clamp(1rem, 5vw, 1.25rem)",
            borderRadius: 8,
            boxShadow: "0 10px 30px rgba(0,0,0,0.6)",
            maxWidth: 320,
            width: "min(320px, calc(100vw - 2.5rem))",
          }}
        >
          {toastMsg}
        </div>
      )}
    </div>
  );
};

export default UserMenu;

type MenuButtonProps = {
  label: string;
  onClick: () => void | Promise<void>;
};

const MenuButton = ({ label, onClick }: MenuButtonProps): JSX.Element => (
  <button
    type="button"
    onClick={onClick}
    style={{
      width: "100%",
      border: "none",
      background: "transparent",
      color: label.includes("Delete") ? "#ff5f6d" : "#f0f4ff",
      fontFamily: '"JetBrains Mono", monospace',
      fontSize: "0.8rem",
      letterSpacing: "0.02em",
      padding: "0.4rem 0.2rem",
      textAlign: "center",
      borderRadius: 8,
      transition: "background 0.2s ease, color 0.2s ease",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = "rgba(255,255,255,0.07)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = "transparent";
    }}
  >
    {label}
  </button>
);
